import React from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageHeader from '../../components/PageHeader';
import PageHeadline from '../../components/PageHeadline';
import AssetDetailSection from '../assets/components/AssetDetailSection';
import DetailField from '../assets/components/DetailField';
import PricingSection from '../assets/components/PricingSection';
import UtilizationSection from '../assets/components/UtilizationSection';
import AssignedObjectsSection from './components/AssignedObjectsSection';
import { PriceScale } from '../../api/asset/assetApiTypes';
import { formatCurrency } from '../../utils';
import { getCurrentLang } from '../../i18n/i18n';
import { makeServicesProductUrl, paths } from '../../routes';
import { productIdToGroupId, useProductFleet } from './productFleet';

/**
 * Product detail — the offering, and the objects that fulfil it.
 *
 * The split this page exists to make: `/assets/:assetId` was titled
 * "object details" while rendering mostly *product* data off `data.Group`
 * (description, price, deposit, images, utilization). Those are properties of
 * what is sold, shared by every physical unit, so they belong here; the
 * object page keeps only what is true of one unit.
 *
 * Everything product-level is **read-only here and links out**. The product
 * form belongs to the Services area (architecture §9a, ADR-0005) and is being
 * built there; this page never embeds it. Until that form and W1.2a land the
 * link opens the right Services tab and ignores the product id — no break,
 * just no pre-selection.
 *
 * Held like the creation wizard: permission-gated, direct route only, not in
 * the sidebar, while the product model (#1712) and its write endpoints (#2134)
 * are outstanding.
 */
const ProductDetailPage: React.FC = () => {
    const { productId } = useParams<{ productId: string }>();
    const { t } = useTranslation();
    const navigate = useNavigate();

    const { product, isLoading, isError } = useProductFleet(productId);

    if (isLoading) {
        return (
            <div className={'p-6 flex items-center justify-center min-h-[400px]'}>
                <div className={'animate-spin rounded-full h-12 w-12 border-b-2 border-primary'} />
            </div>
        );
    }

    if (isError || !productId) {
        return (
            <div className={'p-6 flex flex-col gap-4'}>
                <PageHeader>
                    <PageHeadline>{t('product_detail.not_found.title')}</PageHeadline>
                </PageHeader>
                <div className={'bg-text-contrast rounded-[5px] shadow-sm p-6 flex flex-col items-center gap-4'}>
                    <p className={'text-foreground/60'}>{t('product_detail.not_found.message')}</p>
                    <button
                        onClick={() => navigate(paths.assets)}
                        className={'text-primary hover:underline'}
                    >
                        {t('product_detail.not_found.back_to_overview')}
                    </button>
                </div>
            </div>
        );
    }

    const useCase = product?.useCases?.[0];
    // `PriceScaleTable` takes the asset-detail row shape; the fleet read
    // returns the same runtime shape with Decimals already numbers, so the
    // existing table renders it rather than a second one being written.
    const priceScales = product?.priceCategory?.priceScales as PriceScale[] | undefined;

    return (
        <div className={'p-6 flex flex-col gap-6'}>
            <div className={'sticky top-16 z-30 bg-background -mx-6 px-6 -mt-6 pt-6 pb-3'}>
                <PageHeader>
                    <div className={'flex-grow'}>
                        <PageHeadline>{t('product_detail.title')}</PageHeadline>
                        <p className={'text-sm text-foreground/60'}>
                            {product?.name ?? t('common.not_applicable')}
                        </p>
                    </div>
                </PageHeader>
            </div>

            {/* ═══ The offering ═══ */}
            <div className={'grid grid-cols-1 lg:grid-cols-12 gap-6'}>
                <div className={'lg:col-span-7 flex flex-col gap-6'}>
                    <AssetDetailSection
                        title={t('product_detail.sections.offering')}
                        subtitle={t('product_detail.sections.offering_subtitle')}
                    >
                        <div className={'flex flex-col gap-4'}>
                            <DetailField
                                label={t('product_detail.fields.name')}
                                value={product?.name ?? undefined}
                                emptyLabel={'missing'}
                            />
                            <DetailField
                                label={t('product_detail.fields.description')}
                                value={product?.description?.trim() || undefined}
                                emptyLabel={'na'}
                            />
                            <DetailField
                                label={t('product_detail.fields.deposit')}
                                value={product?.depositAmount != null
                                    ? formatCurrency(product.depositAmount, getCurrentLang())
                                    : undefined}
                                emptyLabel={'na'}
                            />
                            <div>
                                <Link
                                    to={makeServicesProductUrl(productId)}
                                    className={'text-sm text-primary hover:underline'}
                                >
                                    {t('product_detail.edit_in_services')}
                                </Link>
                                <p className={'text-xs text-foreground/60 mt-1'}>
                                    {t('product_detail.edit_in_services_hint')}
                                </p>
                            </div>
                        </div>
                    </AssetDetailSection>
                </div>

                <div className={'lg:col-span-5 flex flex-col gap-6'}>
                    <PricingSection
                        priceCategoryName={product?.priceCategory?.name}
                        priceCategoryId={product?.priceCategory?.id}
                        priceScales={priceScales}
                        pricingDiscounts={undefined}
                        useCase={useCase}
                        mode={'view'}
                    />
                </div>
            </div>

            {/* ═══ The fleet ═══ */}
            <AssignedObjectsSection productId={productId} />

            {/* ═══ Activity ═══
                Capacity over a range is a product-level question, so it uses
                the aggregate occupancy source — the same section the asset
                page renders, reused rather than rebuilt. It cannot say WHICH
                object is busy (no asset id exists in that payload); that is
                the fleet table's job, from a different source. */}
            <UtilizationSection groupId={productIdToGroupId(productId)} />
        </div>
    );
};

export default ProductDetailPage;
